"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { useUserContext } from "../store/contexts/userContext/userContexts";
import { userServices } from "@/app/auth/sign-in/_modules/services/user-services";
import supabaseAuthService from "../services/supabaseAuthService";
import { getApiUrl } from "../services/config";

type UserStatus = 'available' | 'idle' | 'on_call' | 'dnd';

// Storage keys for persistence across refreshes
const STORAGE_KEY_AUTO_IDLE = 'insuracentral_auto_idle_active';
const STORAGE_KEY_PREVIOUS_STATUS = 'insuracentral_previous_status';
const STORAGE_KEY_MANUAL_IDLE_TIME = 'insuracentral_manual_idle_time';
// SHARED across tabs (localStorage): activity in ANY tab of this browser counts.
// Was sessionStorage (per-tab), which made a hidden tab think the agent was idle
// while they were actively clicking in another tab — then its 15-min timer fired
// a browser-wide logout mid-work. localStorage is still per-device/per-browser,
// so cross-device single-session enforcement (backend "last login wins") is unaffected.
const STORAGE_KEY_LAST_ACTIVITY = 'insuracentral_last_activity';
// Backup flag for active call — checked independently of user status
// This is set in sessionStorage so it's tab-scoped and auto-clears on tab close
const STORAGE_KEY_ACTIVE_CALL = 'insuracentral_active_call';
// Flag for active route — prevents idle/logout while agent is driving
const STORAGE_KEY_ACTIVE_ROUTE = 'insuracentral_active_route';

// The active-route flag is now TIME-BOUND. Value is the epoch ms when the
// route was last "alive" (GPS pinged, disposition tapped, /routes opened).
// Stops a stale "in_progress" route from granting forever-protection — a
// dropped/abandoned route's flag expires after the TTL and the agent
// re-enters the normal idle/logout flow.
const ACTIVE_ROUTE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const isActiveRouteFlagFresh = (raw: string | null): boolean => {
  if (!raw) return false;
  // Legacy: old boolean "true" values are treated as expired so the new TTL
  // logic takes over on first re-set.
  if (raw === 'true') return false;
  const ts = parseInt(raw, 10);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < ACTIVE_ROUTE_TTL_MS;
};

// No grace period - restore immediately on any activity
// (Previously had 5-minute grace period for manual idle)

// Default idle timeout (10 minutes)
const DEFAULT_IDLE_TIMEOUT_MINUTES = 10;

// Auto-logout timeout after idle (15 minutes from last activity = 5 minutes after idle starts)
// User goes idle at 10 min, gets logged out at 15 min total inactivity
const DEFAULT_AUTO_LOGOUT_TIMEOUT_MINUTES = 15;

// Warning before logout (2 minutes before logout - so at 13 minutes of inactivity)
const LOGOUT_WARNING_MINUTES = 2;

interface UseAutoStatusManagerOptions {
  idleTimeoutMinutes?: number; // Time in minutes before auto-idle (default: 10)
  enableAutoIdle?: boolean; // Whether to enable auto-idle (default: true)
  autoLogoutMinutes?: number; // Time in minutes before auto-logout (default: 15)
  enableAutoLogout?: boolean; // Whether to enable auto-logout (default: true)
}

// Helper to safely access sessionStorage
const getStoredValue = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredValue = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Silent fail
  }
};

const removeStoredValue = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Silent fail
  }
};

// Shared (localStorage) helpers for flags that must be visible across ALL tabs.
// Active call / active route flags use localStorage so that an idle tab won't
// auto-logout when another tab is on a call.
const getSharedFlag = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSharedFlag = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // Silent fail
  }
};

const removeSharedFlag = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // Silent fail
  }
};

/**
 * Hook to automatically manage user status based on activity and call state
 * - Auto-idle: Sets status to 'idle' after X minutes of inactivity
 * - Auto-on-call: Sets status to 'on_call' when a call starts (handled by call components)
 * - Visibility detection: Restores status when user returns to the tab
 */
export const useAutoStatusManager = (options: UseAutoStatusManagerOptions = {}) => {
  const {
    idleTimeoutMinutes = DEFAULT_IDLE_TIMEOUT_MINUTES, // 10 minutes
    enableAutoIdle = true,
    autoLogoutMinutes = DEFAULT_AUTO_LOGOUT_TIMEOUT_MINUTES, // 15 minutes
    enableAutoLogout = true,
  } = options;

  const { user, refetchUser } = useUserContext();
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoutWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isRestoringRef = useRef<boolean>(false); // Prevent duplicate restore calls
  const lastRestoreAttemptRef = useRef<number>(0); // Debounce restore attempts
  const isPausedRef = useRef<boolean>(false); // Pause idle/logout during long operations (e.g., CSV import)

  // State for logout warning modal
  const [showLogoutWarning, setShowLogoutWarning] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(LOGOUT_WARNING_MINUTES * 60); // seconds

  // Initialize from storage to persist across refreshes
  const isAutoIdleActiveRef = useRef<boolean>(getStoredValue(STORAGE_KEY_AUTO_IDLE) === 'true');
  const previousStatusRef = useRef<UserStatus | null>(
    (getStoredValue(STORAGE_KEY_PREVIOUS_STATUS) as UserStatus) || null
  );

  // Calculate auto-logout timeout in ms
  const autoLogoutTimeoutMs = autoLogoutMinutes * 60 * 1000;
  // For short timeouts (testing), show warning at 50% of total time instead of fixed 2 min before
  // E.g., for 2 min total, warning shows at 1 min (50%), for 15 min total, warning shows at 13 min
  const effectiveWarningMinutes = autoLogoutMinutes <= LOGOUT_WARNING_MINUTES * 2
    ? autoLogoutMinutes / 2  // For short timeouts, warn at 50%
    : LOGOUT_WARNING_MINUTES; // For normal timeouts, warn 2 min before
  const logoutWarningTimeoutMs = Math.max(0, (autoLogoutMinutes - effectiveWarningMinutes) * 60 * 1000);

  const currentStatus: UserStatus = ((user?.user as any)?.userStatus as UserStatus) || 'available';

  // Use ref to always have access to latest status in callbacks (avoids stale closure)
  const currentStatusRef = useRef<UserStatus>(currentStatus);
  currentStatusRef.current = currentStatus;

  // Keep refs for values needed in callbacks
  const userIdRef = useRef<string | undefined>(user?.user?.id);
  userIdRef.current = user?.user?.id;

  const refetchUserRef = useRef(refetchUser);
  refetchUserRef.current = refetchUser;

  // Restore status from idle - separated for reuse
  // This restores from idle on ANY activity, regardless of whether idle was set manually or automatically
  // No grace period - immediately restores on any user activity
  const restoreFromIdle = useCallback(async () => {
    // Debounce: prevent multiple restore attempts within 1 second
    const now = Date.now();
    if (now - lastRestoreAttemptRef.current < 1000) {
      return;
    }
    lastRestoreAttemptRef.current = now;

    // Prevent duplicate calls
    if (isRestoringRef.current) {
      return;
    }

    const status = currentStatusRef.current;
    const userId = userIdRef.current;

    // Token not rehydrated yet (fresh launch / resume): calling the API now
    // guarantees a 401 and leaks the "Status sync failed" toast. Skip quietly —
    // the next touch retries, and by then the session is ready.
    try {
      const session = await supabaseAuthService.getCurrentSession();
      if (!session?.access_token) return;
    } catch {
      return;
    }

    // Don't restore from idle if there's an active call or active route (check all tabs)
    const hasActiveCall = getStoredValue(STORAGE_KEY_ACTIVE_CALL) === 'true' || getSharedFlag(STORAGE_KEY_ACTIVE_CALL) === 'true';
    const hasActiveRoute = isActiveRouteFlagFresh(getStoredValue(STORAGE_KEY_ACTIVE_ROUTE)) || isActiveRouteFlagFresh(getSharedFlag(STORAGE_KEY_ACTIVE_ROUTE));
    if (hasActiveCall || hasActiveRoute) {
      return;
    }

    // Only restore if current status is idle (regardless of how it was set)
    if (status !== 'idle' || !userId) {
      return;
    }

    // If we have a previous status saved (from auto-idle), use it; otherwise default to 'available'
    const statusToRestore = previousStatusRef.current || 'available';

    // Only restore if previous status wasn't idle
    if (statusToRestore === 'idle') {
      // Even if previous was idle, restore to available
      isRestoringRef.current = true;
      try {
        await userServices.updateUserStatus(userId, 'available', { silent: true });
        refetchUserRef.current((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            userStatus: 'available',
            lastManualStatusChangeAt: new Date().toISOString(),
          } as any,
        }));
        // Clear the auto-idle state and storage
        isAutoIdleActiveRef.current = false;
        previousStatusRef.current = null;
        removeStoredValue(STORAGE_KEY_AUTO_IDLE);
        removeStoredValue(STORAGE_KEY_PREVIOUS_STATUS);
        removeStoredValue(STORAGE_KEY_MANUAL_IDLE_TIME);
      } catch {
        // API failed - don't update local state
      } finally {
        isRestoringRef.current = false;
      }
      return;
    }

    isRestoringRef.current = true;

    try {
      // Call API first, only update local state on success
      await userServices.updateUserStatus(userId, statusToRestore, { silent: true });

      // API succeeded, now update local state
      refetchUserRef.current((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          userStatus: statusToRestore,
          lastManualStatusChangeAt: new Date().toISOString(),
        } as any,
      }));

      // Clear the auto-idle state and storage
      isAutoIdleActiveRef.current = false;
      previousStatusRef.current = null;
      removeStoredValue(STORAGE_KEY_AUTO_IDLE);
      removeStoredValue(STORAGE_KEY_PREVIOUS_STATUS);
      removeStoredValue(STORAGE_KEY_MANUAL_IDLE_TIME);
    } catch {
      // API failed - don't update local state, but clear the restoring flag
      // so we can try again on next activity
    } finally {
      isRestoringRef.current = false;
    }
  }, []);

  // Reset activity timer
  // Use refs to always get latest values - this prevents stale closure issues
  // when the callback is attached to event listeners
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    // Get current status from ref (always fresh)
    const status = currentStatusRef.current;

    // If user is currently idle (manual or auto) and has activity, restore to available/previous status
    if (status === 'idle') {
      restoreFromIdle();
    }

    // Clear existing timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    // Set new timeout only if auto-idle is enabled and current status isn't on_call
    // SKIP IDLE only if user is on a call or if idle is paused (e.g., during CSV import)
    // DND users CAN still go idle after inactivity
    if (enableAutoIdle && status !== 'on_call' && !isPausedRef.current) {
      idleTimeoutRef.current = setTimeout(() => {
        // Get fresh status inside timeout callback
        const currentStat = currentStatusRef.current;
        const userId = userIdRef.current;

        if (!userId) return;

        // Skip if paused (e.g., during long-running operations like CSV import)
        if (isPausedRef.current) return;

        // Skip if there's an active call or route in ANY tab — don't go idle while driving
        const hasActiveCall = getStoredValue(STORAGE_KEY_ACTIVE_CALL) === 'true' || getSharedFlag(STORAGE_KEY_ACTIVE_CALL) === 'true';
        const hasActiveRoute = isActiveRouteFlagFresh(getStoredValue(STORAGE_KEY_ACTIVE_ROUTE)) || isActiveRouteFlagFresh(getSharedFlag(STORAGE_KEY_ACTIVE_ROUTE));
        if (hasActiveCall || hasActiveRoute) return;

        // Only auto-idle if not on a call or already idle
        // DND users CAN go idle - we save their DND status to restore later
        if (currentStat !== 'idle' && currentStat !== 'on_call') {
          // Save current status before changing to idle
          previousStatusRef.current = currentStat;
          isAutoIdleActiveRef.current = true;

          // Persist to storage for recovery across refreshes
          setStoredValue(STORAGE_KEY_PREVIOUS_STATUS, currentStat);
          setStoredValue(STORAGE_KEY_AUTO_IDLE, 'true');

          userServices.updateUserStatus(userId, 'idle', { silent: true })
            .then(() => {
              refetchUserRef.current((prev) => ({
                ...prev,
                user: {
                  ...prev.user,
                  userStatus: 'idle',
                  lastManualStatusChangeAt: new Date().toISOString(),
                } as any,
              }));
            })
            .catch(() => {
              // API failed, clear the idle state
              isAutoIdleActiveRef.current = false;
              previousStatusRef.current = null;
              removeStoredValue(STORAGE_KEY_AUTO_IDLE);
              removeStoredValue(STORAGE_KEY_PREVIOUS_STATUS);
            });
        }
      }, idleTimeoutMinutes * 60 * 1000);
    }
  }, [enableAutoIdle, idleTimeoutMinutes, restoreFromIdle]);

  // On mount: if the user's status is 'idle' and we have auto-idle state persisted,
  // immediately restore since the user just refreshed/loaded the page (they're active)
  useEffect(() => {
    if (!enableAutoIdle) return;

    const storedAutoIdle = getStoredValue(STORAGE_KEY_AUTO_IDLE) === 'true';
    const storedPreviousStatus = getStoredValue(STORAGE_KEY_PREVIOUS_STATUS) as UserStatus | null;

    // If we have stored auto-idle state and user is currently idle, they just refreshed
    // while idle - restore them to their previous status since loading the page = activity
    if (storedAutoIdle && storedPreviousStatus && currentStatus === 'idle') {
      // Set the refs from storage
      isAutoIdleActiveRef.current = true;
      previousStatusRef.current = storedPreviousStatus;

      // Small delay to ensure everything is initialized, then restore
      const timeout = setTimeout(() => {
        restoreFromIdle();
      }, 500);

      return () => clearTimeout(timeout);
    } else if (!storedAutoIdle) {
      // No stored auto-idle state but status might be 'idle' - clear any stale storage
      removeStoredValue(STORAGE_KEY_AUTO_IDLE);
      removeStoredValue(STORAGE_KEY_PREVIOUS_STATUS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Auto-logout functionality
  // opts.force skips the shared-clock freshness guard — used ONLY by the explicit
  // "Log Out Now" button in the warning modal, where the click itself is activity.
  const performAutoLogout = useCallback(async (opts: { force?: boolean; silent?: boolean } = {}) => {
    // FRESHNESS GUARD: re-read the SHARED (browser-wide) activity clock before firing.
    // This tab's timer may be stale — the agent could have been active in another tab
    // the whole time (the clock is updated on activity in ANY tab). If the clock is
    // fresh, this logout is a false positive: re-arm the timer instead of signing out.
    if (!opts?.force) {
      const sharedLast = getSharedFlag(STORAGE_KEY_LAST_ACTIVITY);
      const lastTs = sharedLast ? parseInt(sharedLast, 10) : NaN;
      if (Number.isFinite(lastTs) && Date.now() - lastTs < autoLogoutTimeoutMs) {
        resetLogoutTimerRef.current();
        return;
      }
    }

    // SAFETY CHECK: Never auto-logout a user who is on a call
    // Check both the user status AND the backup storage flag
    // The backup flag protects against cases where the status API call failed
    const currentStat = currentStatusRef.current;
    // Check BOTH sessionStorage (this tab) and localStorage (any tab) for active call/route
    const hasActiveCall = getStoredValue(STORAGE_KEY_ACTIVE_CALL) === 'true' || getSharedFlag(STORAGE_KEY_ACTIVE_CALL) === 'true';
    const hasActiveRoute = isActiveRouteFlagFresh(getStoredValue(STORAGE_KEY_ACTIVE_ROUTE)) || isActiveRouteFlagFresh(getSharedFlag(STORAGE_KEY_ACTIVE_ROUTE));
    if (currentStat === 'on_call' || hasActiveCall || hasActiveRoute) {
      // User is on a call or driving a route — reset the logout timer instead of logging out
      // Update last activity so the timer restarts from now
      setSharedFlag(STORAGE_KEY_LAST_ACTIVITY, Date.now().toString());
      return;
    }

    // Nobody signed in, nothing to log out. The root layout mounts this hook on the
    // sign-in page too (ImportProgressProvider); without this an idle sign-in tab
    // "logged out" and reloaded itself every 15 minutes, forever.
    if (!opts?.force) {
      try {
        if (!(await supabaseAuthService.getCurrentSession())?.access_token) {
          removeSharedFlag(STORAGE_KEY_LAST_ACTIVITY);
          return;
        }
      } catch { /* can't tell: log out below */ }
    }

    try {
      // Clear all storage
      removeStoredValue(STORAGE_KEY_AUTO_IDLE);
      removeStoredValue(STORAGE_KEY_PREVIOUS_STATUS);
      removeStoredValue(STORAGE_KEY_MANUAL_IDLE_TIME);
      removeSharedFlag(STORAGE_KEY_LAST_ACTIVITY);

      // Set user status to offline BEFORE signing out
      // This ensures call forwarding works correctly when user is auto-logged out
      // Use supabaseAuthService which has localStorage fallback for multi-device scenarios
      try {
        const currentSession = await supabaseAuthService.getCurrentSession();

        if (currentSession?.access_token) {
          const statusUrl = getApiUrl('users/status');
          await fetch(`${statusUrl}?token=${currentSession.access_token}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status: 'offline' }),
          }).catch(() => {
            // Ignore errors - status update is best-effort
          });
        }
      } catch {
        // Ignore errors - continue with logout
      }

      // Sign out from Supabase — LOCAL scope only. Idle logout should only log
      // out THIS device; global scope would revoke the user's tokens on every
      // device (an idle iPad killing the desktop they're actively working on).
      // Deliberate cross-device eviction ("last login wins") is backend-driven
      // and does not use this path.
      await supabaseAuthService.signOut({ scope: 'local' });

      // IMPORTANT: Use window.location.href instead of router.push to force a full page reload
      // This clears all client-side state (user context, React state, etc.) to prevent
      // stale data from interfering with the next login attempt.
      // Using router.push() was causing users to need to refresh before logging in again.
      // `silent`: the idle window elapsed while no tab was open (boot check) —
      // the person never saw a countdown, so no "logged out due to inactivity"
      // banner; they simply land on sign-in like any signed-out visitor.
      window.location.href = opts.silent ? '/auth/sign-in' : '/auth/sign-in?reason=idle_timeout';
    } catch (error) {
      // Force redirect even if logout fails - use full page reload
      window.location.href = '/auth/sign-in?reason=session_expired';
    }
  }, [autoLogoutTimeoutMs]);

  // Reset logout timer - called on any user activity
  const resetLogoutTimer = useCallback(() => {
    if (!enableAutoLogout) return;

    // SKIP auto-logout if user is on a call OR driving an in-progress route.
    // Door-knocking agents go long stretches without touching the screen
    // (driving, walking up to a door) — they shouldn't be kicked out for
    // "inactivity" mid-route.
    const currentStat = currentStatusRef.current;
    const hasActiveCall = getStoredValue(STORAGE_KEY_ACTIVE_CALL) === 'true' || getSharedFlag(STORAGE_KEY_ACTIVE_CALL) === 'true';
    const hasActiveRoute = isActiveRouteFlagFresh(getStoredValue(STORAGE_KEY_ACTIVE_ROUTE)) || isActiveRouteFlagFresh(getSharedFlag(STORAGE_KEY_ACTIVE_ROUTE));
    if (currentStat === 'on_call' || hasActiveCall || hasActiveRoute) {
      const now = Date.now();
      setSharedFlag(STORAGE_KEY_LAST_ACTIVITY, now.toString());
      setShowLogoutWarning(false);
      if (logoutWarningTimeoutRef.current) {
        clearTimeout(logoutWarningTimeoutRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      return;
    }

    // Store last activity time for persistence across tabs
    const now = Date.now();
    setSharedFlag(STORAGE_KEY_LAST_ACTIVITY, now.toString());

    // Clear warning state
    setShowLogoutWarning(false);
    // Calculate countdown in seconds (time from warning to logout)
    const countdownSeconds = Math.round(effectiveWarningMinutes * 60);
    setLogoutCountdown(countdownSeconds);

    // Clear existing timeouts
    if (logoutWarningTimeoutRef.current) {
      clearTimeout(logoutWarningTimeoutRef.current);
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    // Set warning timeout (shows warning before logout)
    // For 15 min total: warning at 13 min (2 min before logout)
    logoutWarningTimeoutRef.current = setTimeout(() => {
      setShowLogoutWarning(true);
      setLogoutCountdown(countdownSeconds);

      // Start countdown
      const countdownInterval = setInterval(() => {
        setLogoutCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Store interval for cleanup
      (logoutWarningTimeoutRef as any).countdownInterval = countdownInterval;
    }, logoutWarningTimeoutMs);

    // Set logout timeout
    logoutTimeoutRef.current = setTimeout(() => {
      performAutoLogout();
    }, autoLogoutTimeoutMs);
  }, [enableAutoLogout, autoLogoutTimeoutMs, logoutWarningTimeoutMs, effectiveWarningMinutes, performAutoLogout]);

  // Dismiss warning and reset logout timer (user clicked "Stay Logged In")
  const dismissLogoutWarning = useCallback(() => {
    setShowLogoutWarning(false);
    resetLogoutTimer();
  }, [resetLogoutTimer]);

  // Check stored last activity on mount to handle page refreshes
  useEffect(() => {
    if (!enableAutoLogout) {
      return;
    }

    const storedLastActivity = getSharedFlag(STORAGE_KEY_LAST_ACTIVITY);
    if (storedLastActivity) {
      const lastActivityTime = parseInt(storedLastActivity, 10);
      const timeSinceActivity = Date.now() - lastActivityTime;

      // If more than auto-logout time has passed, log out immediately.
      // BUT the shared clock can outlive the SESSION (a leftover from an old
      // visit — this key deliberately survives tab close). Opening a new tab
      // while signed OUT mounted this layout long enough to fire the kick and
      // brand a person who was never logged in with the "logged out due to
      // inactivity" banner. Only fire for a real session; otherwise discard
      // the stale stamp and let the auth guard route them normally.
      if (timeSinceActivity >= autoLogoutTimeoutMs) {
        void (async () => {
          try {
            const session = await supabaseAuthService.getCurrentSession();
            if (!session?.access_token) {
              removeSharedFlag(STORAGE_KEY_LAST_ACTIVITY);
              return;
            }
          } catch { /* can't tell — treat as a real idle logout below */ }
          // A real persisted session whose idle window elapsed while the app
          // was closed: end it (policy unchanged) but SILENTLY — the banner
          // read as "logged out before I even logged in" to returning users.
          performAutoLogout({ silent: true });
        })();
        return;
      }

      // If in warning period, show warning with adjusted countdown
      if (timeSinceActivity >= logoutWarningTimeoutMs) {
        const remainingMs = autoLogoutTimeoutMs - timeSinceActivity;
        setShowLogoutWarning(true);
        setLogoutCountdown(Math.floor(remainingMs / 1000));

        // Set logout timeout for remaining time
        logoutTimeoutRef.current = setTimeout(() => {
          performAutoLogout();
        }, remainingMs);

        return;
      }
    }

    // Initialize logout timer
    resetLogoutTimer();

    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      if (logoutWarningTimeoutRef.current) {
        clearTimeout(logoutWarningTimeoutRef.current);
        if ((logoutWarningTimeoutRef as any).countdownInterval) {
          clearInterval((logoutWarningTimeoutRef as any).countdownInterval);
        }
      }
    };
  // Include autoLogoutMinutes to re-initialize when timeout values change
  }, [enableAutoLogout, autoLogoutMinutes, autoLogoutTimeoutMs, logoutWarningTimeoutMs, performAutoLogout, resetLogoutTimer]);

  // Store callback refs to avoid dependency issues
  const resetIdleTimerRef = useRef(resetIdleTimer);
  resetIdleTimerRef.current = resetIdleTimer;

  const resetLogoutTimerRef = useRef(resetLogoutTimer);
  resetLogoutTimerRef.current = resetLogoutTimer;

  const restoreFromIdleRef = useRef(restoreFromIdle);
  restoreFromIdleRef.current = restoreFromIdle;

  const performAutoLogoutRef = useRef(performAutoLogout);
  performAutoLogoutRef.current = performAutoLogout;

  // Set up activity listeners
  useEffect(() => {
    if (!enableAutoIdle && !enableAutoLogout) return;

    // Activity events that indicate user is active
    // IMPORTANT: 'mousemove' is intentionally EXCLUDED to prevent constant timer resets
    // Mouse movement alone shouldn't be considered "active use" of the application
    // Only intentional actions like clicking, typing, or scrolling count as activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      if (enableAutoIdle) {
        resetIdleTimerRef.current();
        // If a restore attempt failed (throttled, token not yet rehydrated on
        // app resume), the user was stranded on "idle" until the next
        // focus/visibility event — which on mobile may never come. Any touch
        // is proof of presence: retry the restore. Internally guarded (only
        // when status is idle, 1s debounce, single-flight), so this is a
        // no-op for non-idle users.
        restoreFromIdleRef.current();
      }
      if (enableAutoLogout) {
        resetLogoutTimerRef.current();
      }
    };

    // Handle API activity - DISABLED
    // Background API requests (session validation, realtime, etc.) were constantly
    // resetting the logout timer. Only user-initiated actions should reset the timer.
    const handleApiActivity = () => {
      // Intentionally empty - API activity should not reset logout timer
    };

    // ONE return-to-app path, staleness-checked FIRST. Used by visibilitychange,
    // window focus, AND the native Capacitor resume event. On iOS app-resume the
    // webview fires `focus` alongside `visibilitychange`; the old focus handler
    // reset the logout timer unconditionally, stamping the shared activity clock
    // to "now" and forgiving the whole backgrounded stretch before the staleness
    // check could run — the app never auto-signed-out on iOS. Timers are also
    // frozen while backgrounded, so this resume check is the ONLY enforcement
    // point there; every path into it must check the clock before forgiving.
    const handleReturnToApp = () => {
      if (enableAutoLogout) {
        const storedLastActivity = getSharedFlag(STORAGE_KEY_LAST_ACTIVITY);
        if (storedLastActivity) {
          const lastActivityTime = parseInt(storedLastActivity, 10);
          const timeSinceActivity = Date.now() - lastActivityTime;
          if (Number.isFinite(lastActivityTime) && timeSinceActivity >= autoLogoutTimeoutMs) {
            performAutoLogoutRef.current();
            return;
          }
        }
        resetLogoutTimerRef.current();
      }
      if (enableAutoIdle) {
        restoreFromIdleRef.current();
        resetIdleTimerRef.current();
      }
    };

    // Handle visibility change (tab switch, browser minimize, app background/resume)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleReturnToApp();
      }
    };

    // Handle window focus (when user clicks back into the window)
    const handleFocus = handleReturnToApp;

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Add API activity listener (fired by axios on successful responses)
    window.addEventListener('api-activity', handleApiActivity);

    // Add visibility and focus listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Native app (Capacitor): visibilitychange/focus are not guaranteed to fire
    // on every iOS app-resume — subscribe to the OS-level resume event too. All
    // three funnel into the same staleness-checked handler, so double-firing is
    // harmless.
    let removeNativeResume: (() => void) | undefined;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App: CapApp } = await import('@capacitor/app');
        const sub = await CapApp.addListener('resume', handleReturnToApp);
        removeNativeResume = () => { void sub.remove(); };
      } catch {
        // Plugin unavailable (web build) — visibility/focus listeners still cover it
      }
    })();

    // Initialize timers
    if (enableAutoIdle) {
      resetIdleTimerRef.current();
    }

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('api-activity', handleApiActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      removeNativeResume?.();
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      if (logoutWarningTimeoutRef.current) {
        clearTimeout(logoutWarningTimeoutRef.current);
      }
    };
  }, [enableAutoIdle, enableAutoLogout, autoLogoutTimeoutMs]); // Removed callback dependencies - using refs instead

  // Function to manually set status to on_call (to be called by dialer)
  const setOnCallStatus = useCallback(async () => {
    const status = currentStatusRef.current;
    const userId = userIdRef.current;

    if (!userId) return;

    // Set backup flag so auto-logout can detect active call even if status update fails
    // Write to BOTH sessionStorage (this tab) and localStorage (all tabs)
    setStoredValue(STORAGE_KEY_ACTIVE_CALL, 'true');
    setSharedFlag(STORAGE_KEY_ACTIVE_CALL, 'true');

    // Clear idle/logout timers while on a call — user is actively engaged
    // This prevents auto-logout from firing mid-call when there's no mouse/keyboard activity
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (logoutWarningTimeoutRef.current) {
      clearTimeout(logoutWarningTimeoutRef.current);
      if ((logoutWarningTimeoutRef as any).countdownInterval) {
        clearInterval((logoutWarningTimeoutRef as any).countdownInterval);
      }
      logoutWarningTimeoutRef.current = null;
    }
    setShowLogoutWarning(false);

    // Save current status if not already on call
    if (status !== 'on_call') {
      // If we were auto-idle, use the previous status from before idle
      // Otherwise use current status
      const statusBeforeCall = isAutoIdleActiveRef.current
        ? (previousStatusRef.current || 'available')
        : status;
      previousStatusRef.current = statusBeforeCall;

      // Update local state immediately so the UI reflects on_call right away
      refetchUserRef.current((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          userStatus: 'on_call',
          lastManualStatusChangeAt: new Date().toISOString(),
        } as any,
      }));
      // Clear auto-idle state since we're now on a call
      isAutoIdleActiveRef.current = false;
      removeStoredValue(STORAGE_KEY_AUTO_IDLE);
      removeStoredValue(STORAGE_KEY_PREVIOUS_STATUS);

      try {
        await userServices.updateUserStatus(userId, 'on_call', { silent: true });
      } catch {
        // API failed but local state is already updated — backup flag protects against auto-logout
      }
    }
  }, []); // No dependencies - uses refs for fresh values

  // Function to restore status after call ends
  const restoreStatusAfterCall = useCallback(async () => {
    const status = currentStatusRef.current;
    const userId = userIdRef.current;

    if (!userId) {
      // Still clear the flag even if no userId
      removeStoredValue(STORAGE_KEY_ACTIVE_CALL);
      removeSharedFlag(STORAGE_KEY_ACTIVE_CALL);
      return;
    }

    if (status === 'on_call') {
      const statusToRestore = previousStatusRef.current || 'available';

      // DELAY: Keep on_call status for 3 seconds after call ends to prevent
      // incoming call popups from flashing during the transition between calls
      // (e.g., between ending a manual call and starting a power dialer session).
      // The IncomingCallContext checks userStatusRef === 'on_call' to block popups.
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Update local state FIRST, then clear the active call flag
      refetchUserRef.current((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          userStatus: statusToRestore,
          lastManualStatusChangeAt: new Date().toISOString(),
        } as any,
      }));

      // NOW clear the active call flag — status ref is already updated
      removeStoredValue(STORAGE_KEY_ACTIVE_CALL);
      removeSharedFlag(STORAGE_KEY_ACTIVE_CALL);

      // Reset idle and logout timers after call ends
      resetIdleTimer();
      resetLogoutTimer();

      try {
        await userServices.updateUserStatus(userId, statusToRestore, { silent: true });
      } catch (error) {
        // API failed but local state is already updated — will sync on next successful call
      }
    } else {
      // Status wasn't on_call (maybe already restored by another path) — still clear the flag
      removeStoredValue(STORAGE_KEY_ACTIVE_CALL);
      removeSharedFlag(STORAGE_KEY_ACTIVE_CALL);
    }
  }, [resetIdleTimer, resetLogoutTimer]); // Uses refs for other values

  // Pause idle/logout timers during long-running operations (e.g., CSV import)
  const pauseIdleTimer = useCallback(() => {
    isPausedRef.current = true;
    // Clear existing timers
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (logoutWarningTimeoutRef.current) {
      clearTimeout(logoutWarningTimeoutRef.current);
      logoutWarningTimeoutRef.current = null;
    }
    setShowLogoutWarning(false);
  }, []);

  // Resume idle/logout timers after long-running operation completes
  const resumeIdleTimer = useCallback(() => {
    isPausedRef.current = false;
    // Reset activity time and restart timers
    lastActivityRef.current = Date.now();
    resetIdleTimer();
    if (enableAutoLogout) {
      // Manually trigger logout timer reset
      const now = Date.now();
      setSharedFlag(STORAGE_KEY_LAST_ACTIVITY, now.toString());
    }
  }, [resetIdleTimer, enableAutoLogout]);

  return {
    setOnCallStatus,
    restoreStatusAfterCall,
    resetIdleTimer,
    // Pause/resume for long operations (e.g., CSV import)
    pauseIdleTimer,
    resumeIdleTimer,
    // Auto-logout related
    showLogoutWarning,
    logoutCountdown,
    dismissLogoutWarning,
    performAutoLogout,
  };
};
